import { ButtonBase, styled } from '@mui/material';
import {
    ASSIGNMENT_LIST_MIN_HEIGHT,
    ASSIGNMENT_LIST_TRANSITION_MS,
} from 'components/Navigation/assignmentListScrollConstants';
import { getActiveAssignmentId } from 'components/Navigation/navigation-utils';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollFadeContainer, Stack, Text, Tooltip } from 'shell/components';

const AssignmentNavigationList = ({
    assignments = [],
    containerRef,
    expanded,
    onNavigate,
    open,
    pathname,
    scrollable = true,
}) => {
    const { t } = useTranslation();
    const activeAssignmentId = getActiveAssignmentId(pathname, assignments);
    const activeAssignmentRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;

        const scrollActiveAssignment = () =>
            activeAssignmentRef.current?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });

        const frame = window.requestAnimationFrame(scrollActiveAssignment);
        const transitionTimer = window.setTimeout(scrollActiveAssignment, ASSIGNMENT_LIST_TRANSITION_MS);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(transitionTimer);
        };
    }, [activeAssignmentId, assignments.length, open]);

    if (!assignments.length) return null;

    return (
        <AssignmentListExpansion aria-hidden={!open} open={open} ref={containerRef} scrollable={scrollable}>
            <ScrollFadeContainer scrollable={scrollable} fadeHeight={17}>
                <AssignmentListRoot expanded={expanded} role={'list'}>
                    {assignments.map((assignment) => {
                        const hasTitle = Boolean(assignment.title?.trim());
                        const title = hasTitle
                            ? assignment.title.trim()
                            : t('nav.untitled-assignment', 'Untitled assignment');
                        const active = String(assignment.id) === activeAssignmentId;
                        return (
                            <AssignmentNavigationListItem key={assignment.id} role={'listitem'}>
                                <Tooltip placement={'right'} title={title}>
                                    <AssignmentNavigationButton
                                        aria-current={active ? 'page' : undefined}
                                        aria-label={title}
                                        active={active}
                                        ref={active ? activeAssignmentRef : null}
                                        tabIndex={open ? undefined : -1}
                                        onClick={() => onNavigate(`/assignments/${assignment.id}`)}
                                    >
                                        <Text
                                            noWrap
                                            color={hasTitle ? 'inherit' : 'light'}
                                            sx={hasTitle ? undefined : { fontStyle: 'italic' }}
                                            variant={'p3'}
                                        >
                                            {title}
                                        </Text>
                                    </AssignmentNavigationButton>
                                </Tooltip>
                            </AssignmentNavigationListItem>
                        );
                    })}
                </AssignmentListRoot>
            </ScrollFadeContainer>
        </AssignmentListExpansion>
    );
};

const AssignmentListExpansion = styled('div', {
    shouldForwardProp: (prop) => !['open', 'scrollable'].includes(prop),
})(({ open, scrollable, theme }) => ({
    display: 'grid',
    gridTemplateRows: open ? '1fr' : '0fr',
    flex: scrollable ? '1 1 96px' : '0 0 auto',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    marginTop: open ? theme.spacing(1) : 0,
    overflow: 'hidden',
    visibility: open ? 'visible' : 'hidden',
    pointerEvents: open ? 'auto' : 'none',
    transition: `grid-template-rows ${ASSIGNMENT_LIST_TRANSITION_MS}ms ease-out, margin-top ${ASSIGNMENT_LIST_TRANSITION_MS}ms ease-out, visibility 0s linear ${open ? '0s' : `${ASSIGNMENT_LIST_TRANSITION_MS}ms`}`,
    '& > *': { minHeight: 0, ...(scrollable && { maxHeight: '100%' }) },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
}));

const AssignmentListRoot = styled(Stack)(({ expanded, theme }) => ({
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    alignItems: 'stretch',
    minHeight: ASSIGNMENT_LIST_MIN_HEIGHT,
    gap: theme.spacing(0.5),
    paddingLeft: expanded ? theme.spacing(6) : theme.spacing(1),
    paddingRight: expanded ? theme.spacing(4) : theme.spacing(1),
}));

const AssignmentNavigationListItem = styled('div')({ minWidth: 0 });

const AssignmentNavigationButton = styled(ButtonBase)(({ active, theme }) => ({
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: 32,
    padding: theme.spacing(1, 2),
    justifyContent: 'flex-start',
    borderRadius: 8,
    color: active ? theme.palette.ensightful.primary[900] : theme.palette.ensightful.neutral[700],
    background: active ? theme.palette.ensightful.primary[50] : 'transparent',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover': {
        color: theme.palette.ensightful.primary[500],
        background: active ? theme.palette.ensightful.primary[50] : 'transparent',
    },
    '&:focus-visible': { outline: `${theme.palette.ensightful.secondary[400]} solid 3px`, outlineOffset: -2 },
    '& > *': { minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' },
}));

export default AssignmentNavigationList;
